import { CountField } from '@/types/forms/fields/countField';
import { FormList } from '@/types/forms/fields/formList';
import { TextField } from '@/types/forms/fields/textField';
import { ManufacturerAndModuleTypeFormGroup } from '@/types/forms/formGroups/plantPerformance/manufacturerAndModuleTypeFormGroup';
import { required } from 'vuelidate/lib/validators';
import { float } from '@/validators/float';
import { positive } from '@/validators/positive';
import { FormField } from '@/types/forms/fields/formField';
import { FormElement } from '@/types/forms/formElement';
import { InverterFormGroup } from '@/types/forms/formGroups/plantPerformance/inverter/inverterFormGroup';
import { InverterModuleFormGroup } from '@/types/forms/formGroups/plantPerformance/inverter/inverterModuleFormGroup';
import { ActivePowerAndApparentPowerFormGroup } from '@/types/forms/formGroups/plantPerformance/activePowerAndApparentPowerFormGroup';
import { apparentPowerMustBeBiggerThanActualPower } from '@/validators/apparentPowerMustBeBiggerThanActualPower';
import { maxApparentPowerPlugInGeneratingPlant } from '@/validators/maxApparentPowerPlugInGeneratingPlant';
import { restrictedApparentPower } from './boundaryValues';
import { SelectField } from '@/types/forms/fields/selectField';
import { ZerezIdAndNormFormGroup } from '@/types/forms/formGroups/plantPerformance/zerezIdAndNormFormGroup';
import { CheckboxField } from '@/types/forms/fields/checkboxField';
import { contentServices } from '@/services/contentService';
import { distinct } from '@/utilities/utils';
import { FormOption } from '@/types/forms/formOption';
import { HiddenFormField } from '@/types/forms/fields/hiddenFormField';
import { ConnectionRulesType } from '@/types/forms/shared/connectionRulesType';
import { alwaysFalse } from '@/validators/alwaysFalse';

export const inverterFormGroup = new InverterFormGroup({
    inverterHeadline: new FormElement(),
    inverterModules: new FormList(InverterModuleFormGroup, {
        initializeItems: 1,
        minItemCount: 1,
        maxItemCount: 5,
        id: new FormField<string>(),
        inverterManufacturerAndModuleType: new ManufacturerAndModuleTypeFormGroup(
            {
                manufacturer: new HiddenFormField<string>({
                    userChangeable: false,
                    validators: {
                        required,
                    },
                }),
                manufacturerText: new TextField({
                    excludeFromDto: true,
                    excludeFromSchema: true,
                    validators: {
                        required,
                    },
                }),
                manufacturerSelect: new SelectField<string>({
                    excludeFromDto: true,
                    excludeFromSchema: true,
                    hasServiceDrivenOptions: true,
                    onMountOptionsSetter: async () => {
                        const content = contentServices.form.content;
                        if (!content) {
                            throw 'Content unavailable';
                        }

                        const invertersContent =
                            content.serviceDrivenContent.invertersContent;
                        if (!invertersContent) {
                            throw 'Inverter Data unavailable';
                        }

                        const values = distinct(
                            invertersContent.map(
                                (inverter) => inverter.manufacturerName,
                            ),
                        ) as string[];

                        return values.map(
                            (manufacturerName) =>
                                new FormOption({ key: manufacturerName }),
                        );
                    },
                    validators: {
                        required,
                    },
                }),
                moduleType: new HiddenFormField<string>({
                    userChangeable: false,
                    validators: {
                        required,
                    },
                }),
                moduleTypeText: new TextField({
                    excludeFromDto: true,
                    excludeFromSchema: true,
                    validators: {
                        required,
                    },
                }),
                moduleTypeSelect: new SelectField<string>({
                    excludeFromDto: true,
                    excludeFromSchema: true,
                    hasServiceDrivenOptions: true,
                    onMountOptionsSetter: async () => {
                        const content = contentServices.form.content;
                        if (!content) {
                            throw 'Content unavailable';
                        }

                        const invertersContent =
                            content.serviceDrivenContent.invertersContent;
                        if (!invertersContent) {
                            throw 'Inverter Data unavailable';
                        }

                        const values = distinct(
                            invertersContent.map(
                                (inverter) => inverter.moduleType,
                            ),
                        ) as string[];

                        return values.map(
                            (manufacturerName) =>
                                new FormOption({ key: manufacturerName }),
                        );
                    },
                    validators: {
                        required,
                    },
                }),
            },
        ),
        inverterZerezIdAndNorm: new ZerezIdAndNormFormGroup({
            zerezId: new SelectField<string>({
                hasServiceDrivenOptions: true,
                onMountOptionsSetter: async () => {
                    const content = contentServices.form.content;
                    if (!content) {
                        throw 'Content unavailable';
                    }

                    const invertersContent =
                        content.serviceDrivenContent.invertersContent;
                    if (!invertersContent) {
                        throw 'Inverter Data unavailable';
                    }

                    const values = distinct(
                        invertersContent.map((inverter) => inverter.zerezId),
                    ) as string[];

                    return values.map(
                        (zerezId) => new FormOption({ key: zerezId }),
                    );
                },
                validators: {
                    required,
                },
            }),
            norm: new HiddenFormField<ConnectionRulesType[]>({
                jsonSchema: {
                    type: 'array',
                    items: {
                        enum: Object.keys(ConnectionRulesType),
                    },
                },
            }),
            normText: new TextField({
                excludeFromDto: true,
                excludeFromSchema: true,
                userChangeable: false,
            }),
        }),
        inverterManualInputManufacturerTypeZerezId: new CheckboxField(),
        unitCertificateInvalidForInstalledPower: new FormElement({
            validators: {
                alwaysFalse,
            },
        }),
        inverterQuantity: new CountField({
            value: 1,
            min: 1,
            validators: {
                required,
            },
        }),
        inverterActivePowerAndApparentPower: new ActivePowerAndApparentPowerFormGroup(
            {
                activePower: new TextField({
                    validators: {
                        required,
                        float,
                        positive,
                        apparentPowerMustBeBiggerThanActualPower,
                    },
                }),
                apparentPower: new TextField({
                    validators: {
                        required,
                        float,
                        positive,
                    },
                }),
            },
        ),
        inverterTotalActivePower: new FormField({
            value: 0,
        }),
        inverterTotalApparentPower: new FormField({
            value: 0,
            validators: {
                maxApparentPowerPlugInGeneratingPlant: maxApparentPowerPlugInGeneratingPlant(
                    restrictedApparentPower,
                ),
            },
        }),
    }),
    inverterOverallActivePower: new FormField({
        value: 0,
    }),
    inverterOverallActivePowerWarning: new FormElement(),
    inverterOverallApparentPower: new FormField({
        value: 0,
    }),
});
